import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { backendUrl } from "@/lib/api";
import { toast } from "sonner";
import { X, FileText, FileImage, File as FileIcon, ExternalLink, Upload, Loader2, FolderOpen, Pencil, Check, Trash2 } from "lucide-react";
import ConfirmDialog from "@/components/ConfirmDialog";
import UploadDialog from "@/components/UploadDialog";

const token = () => localStorage.getItem("rb_token");
const thumbUrl = (id) => `${backendUrl}/api/drive/${id}/thumbnail?token=${token()}`;
const isImage = (m) => (m || "").startsWith("image/");
function iconFor(m) {
  if (isImage(m)) return FileImage;
  if ((m || "").includes("pdf") || (m || "").includes("word") || (m || "").includes("document")) return FileText;
  return FileIcon;
}

export default function ClienteDocsModal({ cliente, onClose }) {
  const qc = useQueryClient();
  const [uploadOpen, setUploadOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState("");
  const [toDelete, setToDelete] = useState(null);
  const { data, isLoading } = useQuery({
    queryKey: ["drive-files", cliente.id],
    queryFn: async () => (await api.get(`/clienti/${cliente.id}/drive-files`)).data,
  });
  const files = data?.files || [];
  const connected = data?.connected;
  const refresh = () => qc.invalidateQueries({ queryKey: ["drive-files", cliente.id] });

  const startEdit = (e, f) => { e.preventDefault(); e.stopPropagation(); setEditId(f.id); setEditName(f.name); };
  const saveEdit = async (e) => {
    e.preventDefault(); e.stopPropagation();
    try {
      await api.post(`/drive/${editId}/rename`, { name: editName });
      toast.success("Documento rinominato");
      setEditId(null); refresh();
    } catch (err) { toast.warning(err?.response?.data?.detail || "Rinomina non riuscita"); }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/drive/${toDelete.id}`);
      toast.success("Documento spostato nel cestino di Drive");
      setToDelete(null); refresh();
    } catch (err) { toast.error(err?.response?.data?.detail || "Eliminazione non riuscita"); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#16233c]/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid="cliente-docs-modal">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-3xl p-4 border-t-2 border-[#C9A84C] shadow-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between mb-1">
          <div>
            <p className="text-xs uppercase tracking-[0.15em] text-[#4a5568]">Documenti cliente</p>
            <h3 className="font-serif text-2xl text-[#1A2B4A]">{cliente.cognome} {cliente.nome}</h3>
          </div>
          <button data-testid="docs-modal-close" onClick={onClose} className="text-[#1A2B4A] hover:text-[#9b2c2c]"><X className="w-6 h-6" /></button>
        </div>

        <div className="flex items-center gap-3 mb-5">
          {cliente.drive_folder && (
            <a href={cliente.drive_folder} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs text-[#1A2B4A] hover:text-[#C9A84C]">
              <FolderOpen className="w-4 h-4" /> Apri cartella Drive
            </a>
          )}
          <button data-testid="docs-upload-btn" onClick={() => setUploadOpen(true)}
            className="inline-flex items-center gap-1.5 text-xs bg-[#1A2B4A] text-white px-3 py-1.5 rounded-sm hover:bg-[#22345c] transition-colors">
            <Upload className="w-3.5 h-3.5" /> Carica documenti
          </button>
        </div>

        {!connected && <p className="text-sm text-[#9b2c2c]">Collega Google in Impostazioni per vedere i documenti.</p>}
        {connected && isLoading && (
          <div className="flex items-center gap-2 text-sm text-[#4a5568]"><Loader2 className="w-4 h-4 animate-spin text-[#C9A84C]" /> Carico i documenti…</div>
        )}
        {connected && !isLoading && files.length === 0 && (
          <p className="text-sm text-[#4a5568]">Nessun documento nella cartella. Usa "Carica documenti" per aggiungerne.</p>
        )}

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {files.map((f) => {
            const Icon = iconFor(f.mimeType);
            const editing = editId === f.id;
            return (
              <div key={f.id} data-testid="doc-thumb" className="group relative border border-[#1A2B4A]/10 rounded-sm overflow-hidden hover:border-[#C9A84C] transition-colors">
                <a href={f.webViewLink} target="_blank" rel="noreferrer" className="block">
                  <div className="aspect-square bg-[#f9f8f5] flex items-center justify-center overflow-hidden">
                    <img src={thumbUrl(f.id)} alt={f.name} className="w-full h-full object-cover"
                      onError={(e) => { e.target.style.display = "none"; e.target.nextSibling.style.display = "flex"; }} />
                    <div className="w-full h-full items-center justify-center" style={{ display: "none" }}>
                      <Icon className="w-10 h-10 text-[#1A2B4A]/40" strokeWidth={1.2} />
                    </div>
                  </div>
                </a>
                <div className="absolute top-1.5 right-1.5 z-10 flex gap-1 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                  <button data-testid="doc-rename-btn" onClick={(e) => startEdit(e, f)} title="Rinomina"
                    className="bg-white/90 border border-[#1A2B4A]/10 rounded-sm p-1">
                    <Pencil className="w-3.5 h-3.5 text-[#1A2B4A]" />
                  </button>
                  <button data-testid={`doc-delete-${f.id}`} onClick={(e) => { e.preventDefault(); e.stopPropagation(); setToDelete(f); }}
                    title="Elimina documento" className="bg-white/90 border border-[#1A2B4A]/10 rounded-sm p-1">
                    <Trash2 className="w-3.5 h-3.5 text-[#9b2c2c]" />
                  </button>
                </div>
                <div className="p-2.5">
                  {editing ? (
                    <div className="flex gap-1">
                      <input data-testid="doc-rename-input" autoFocus value={editName} onChange={(e) => setEditName(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && saveEdit(e)}
                        className="flex-1 min-w-0 px-2 py-1 text-xs border border-[#C9A84C] rounded-sm focus:outline-none" />
                      <button data-testid="doc-rename-save" onClick={saveEdit} className="text-[#276749]"><Check className="w-4 h-4" /></button>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center justify-between gap-1">
                        <span className="text-xs truncate text-[#1a1a1a]" title={f.name}>{f.name}</span>
                        <ExternalLink className="w-3 h-3 text-[#4a5568] shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                      <div className="mt-1 flex items-center gap-1">
                        <span className="text-[10px] uppercase tracking-wide text-[#C9A84C]">{f.categoria}</span>
                        {f.modifiedTime && <span className="text-[10px] text-[#4a5568]">· {new Date(f.modifiedTime).toLocaleDateString("it-IT")}</span>}
                      </div>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {uploadOpen && <UploadDialog cliente={cliente} onClose={() => setUploadOpen(false)} onUploaded={refresh} />}
      {toDelete && (
        <ConfirmDialog title="Eliminare il documento?"
          message={`"${toDelete.name}" verrà spostato nel cestino di Google Drive (recuperabile per 30 giorni).`}
          confirmLabel="Sposta nel cestino" onConfirm={doDelete} onClose={() => setToDelete(null)} />
      )}
    </div>
  );
}
